import React, { useEffect, useState } from "react";
import "./SectorialIndices.css";
import { environment } from "environment/environment";
import useStockApiWebsocket from "service/websocket/StockApiWebsocket";
import FOTopGainerLooser from "../fo-top-gainer-looser/FOTopGainerLooser";
import SectorPerformance from "../sector-performance/SectorPerformance";

export default function SectorialIndices() {

    // WebSocket connection
    const { sectorialIndicesData, strategyIndicesData } = useStockApiWebsocket(
        environment.stockApiDayRangeDataWSUrl
    );

    const [sectorData, setSectorData] = useState([]);
    const [strategyData, setStrategyData] = useState([]);
    const [activeTab, setActiveTab] = useState("SECTORIAL_INDICES");

    /* ============================================== */

    useEffect(() => {
        if (Array.isArray(sectorialIndicesData) && sectorialIndicesData.length > 0) {
            setSectorData(sectorialIndicesData);
            console.log("Sector Data:", sectorialIndicesData);
        }
        // Strategy data
        if (Array.isArray(strategyIndicesData) && strategyIndicesData.length > 0) {
            setStrategyData(strategyIndicesData);
            console.log("Strategy Data:", strategyData);
        }
    }, [sectorialIndicesData, strategyIndicesData]);

    const toNumber = (value) => {
        const parsedValue = Number.parseFloat(
            String(value ?? "")
                .replace(/,/g, "")
                .replace("%", "")
        );

        return Number.isFinite(parsedValue) ? parsedValue : null;
    };

    const getTopAndBottomCHP = (sectors) => {
        if (!Array.isArray(sectors) || sectors.length < 2) return {};

        const sorted = sectors
            .map((sector) => ({
                symbol: sector?.index,
                chp: toNumber(sector?.pChange),
            }))
            .filter((sector) => sector.chp != null)
            .sort((a, b) => a.chp - b.chp);

        if (sorted.length < 2) return {};

        return {
            firstLowest: sorted[0],
            secondLowest: sorted[1],
            thirdLowest: sorted[2],

            firstHighest: sorted[sorted.length - 1],
            secondHighest: sorted[sorted.length - 2],
            thirdHighest: sorted[sorted.length - 3],
        };
    };

    const getHeaderSymbol = (value) =>
        String(value ?? "")
            .split(":")
            .pop()
            ?.split("-")[0]
            ?.trim() || "--";

    // Header Data
    const headerSource = sectorData?.[0] ?? {};
    const headerStats = getTopAndBottomCHP(sectorData);
    const headerData = [
        {
            label1: "1st HS",
            value1: getHeaderSymbol(headerStats?.firstHighest?.symbol),
            label2: "CHP",
            value2: headerStats?.firstHighest?.chp ?? "--",
        },
        {
            label1: "2nd HS",
            value1: getHeaderSymbol(headerStats?.secondHighest?.symbol),
            label2: "CHP",
            value2: headerStats?.secondHighest?.chp ?? "--",
        },
        {
            label1: "1st LS",
            value1: getHeaderSymbol(headerStats?.firstLowest?.symbol),
            label2: "CHP",
            value2: headerStats?.firstLowest?.chp ?? "--",
        },
        {
            label1: "2nd LS",
            value1: getHeaderSymbol(headerStats?.secondLowest?.symbol),
            label2: "CHP",
            value2: headerStats?.secondLowest?.chp ?? "--",
        },
        {
            label1: "Date",
            value1: headerSource?.localDate ?? headerSource?.createdDate ?? headerSource?.date ?? "--",
            label2: "Time",
            value2: headerSource?.localTime ?? headerSource?.createdTime ?? headerSource?.time ?? "--",
        },
    ];

    // Helper for color intensity
    const getCardStyle = (pChange) => {
        if (pChange > 2) return { backgroundColor: "#0f7a3f" };
        if (pChange > 1) return { backgroundColor: "#1f9d55" };
        if (pChange > 0) return { backgroundColor: "#7bc47f" };
        if (pChange < -2) return { backgroundColor: "#b71c1c" };
        if (pChange < -1) return { backgroundColor: "#e57373" };
        if (pChange < 0) return { backgroundColor: "#f28b82" };
        return { backgroundColor: "#9e9e9e" };
    };

    const getChpColor = (value) => {
        const num = toNumber(value);
        if (num == null) return "white";
        if (num > 0) return "#0dc80d";   // green
        if (num < 0) return "rgb(228, 67, 136)";   // red
        return "white";
    };
    const leftData = headerData.filter(item => item.label1 !== "Date");
    const rightData = headerData.find(item => item.label1 === "Date");

    const SECTOR_SELECTED_STOCK_SET = new Set([
        "UNOMINDA", "BAJAJ-AUTO", "TIINDIA", "BOSCHLTD", "SONACOMS", "TVSMOTOR", "EICHERMOT", "HEROMOTOCO", "M&M", "BHARATFORG",
        "FORCEMOTOR", "PFC", "RECLTD", "BAJAJFINSV", "MFSL", "LICHSGFIN", "JIOFIN", "CHOLAIFIN", "SHRIRAMFIN", "MUTHOOTFIN",
        "BSE", "SBIN", "BAJAJFINANCE", "DABUR", "TATACONSUM", "HINDUNILVR", "GODREJCP", "UNITEDSPR", "NESTLEIND", "ITC",
        "VBL", "UBL", "BRITANNIA", "COFORGE", "PERSISTENT", "LTM", "INFY", "HCLTECH", "TECHMM", "MPHASIS", "TCS", "OFSS",
        "NATIONALUM", "HINDCOPPER", "HINDZINC", "ADANIENT", "JSWSTEEL", "JINDALSTEL", "HINDALCO", "APLAPOLLO", "TATASTEEL",
        "VEDL", "MANKIND", "LAURUSLABS", "SUNPHARMA", "TORNTPHARM", "AUROPHARMA", "DIVISLAB", "GLENMARK", "ALKEM", "BIOCON",
        "DRREDDY", "CIPLA", "LUPIN", "OBEROIRLTY", "PRESTIGE", "GODREJPROP", "LODHA", "DLF", "TITAN", "AMBER", "HAVELLS",
        "DIXON", "VOLTAS", "BLUESTARCO", "RELIANCE", "OIL", "ATGL", "ONGC", "BPCL", "PIDILITIND", "SRF", "PIIND", "CGPOWER",
        "NTPC", "TORNTPOWER", "JSWENERGY", "TATAPOWER", "SIEMENS", "ADANIGREEN", "ADANIENSOL", "ABB", "ADANIPOWER",
        "COALINDIA", "POWERINDIA", "HAL", "BEL", "BDL", "BHEL", "MAZDOCK", "COCHINSHIP", "DALBHARAT", "POLYCAB",
        "PNBHOUSING", "DMART", "360ONE", "ETERNAL", "ASIANPAINT", "LT", "HDFCBANK", "BANDHANBNK", "INDUSINDBK", "TECHM",
        "KOTAKBANK", "ICICIBANK", "AXISBANK"
    ]);
    return (
        <div className="sectorial-indicies">

            {/* ================= HEADER ================= */}
            <header className="header">
                <nav className="nav-container">
                    <div className="header-flex">

                        {/* ===== LEFT SIDE ===== */}
                        <div className="left-nav">

                            <div className="logo-container">
                                <img
                                    src="/assets/images/noptionwave-logo-1.png"
                                    className="header-logo"
                                    alt="logo"
                                />
                            </div>

                            {leftData.map((item, idx) => {
                                const color =
                                    item.label2 === "CHP"
                                        ? getChpColor(item.value2)
                                        : "white";

                                return (
                                    <div className="header-item" key={idx}>
                                        <span className="header-line" style={{ color }}>
                                            {item.label1} : {item.value1}
                                        </span>

                                        <span className="header-line" style={{ color }}>
                                            {item.label2} : {item.value2}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>

                        {/* ===== RIGHT SIDE (DATE & TIME) ===== */}
                        {rightData && (
                            <div className="right-nav">
                                <span className="header-line" style={{ color: "white" }}>
                                    {rightData.label1} : {rightData.value1}
                                </span>
                                <span className="header-line" style={{ color: "white" }}>
                                    {rightData.label2} : {rightData.value2}
                                </span>
                            </div>
                        )}

                    </div>
                </nav>
            </header>

            {/* ===== TABS ===== */}
            <div className="tab-container">
                <div className="tab">

                    <button className={`tablinks sub-header-font ${activeTab === "SECTORIAL_INDICES" ? "active" : ""}`}
                        onClick={() => setActiveTab("SECTORIAL_INDICES")}  >
                        SECTORIAL INDICES
                    </button>

                    <button className={`tablinks sub-header-font ${activeTab === "BEST_STOCK" ? "active" : ""}`}
                        onClick={() => setActiveTab("BEST_STOCK")}  >
                        BEST STOCK
                    </button>

                    <button className={`tablinks sub-header-font ${activeTab === "SECTOR_PERFORMANCE" ? "active" : ""}`}
                        onClick={() => setActiveTab("SECTOR_PERFORMANCE")}  >
                        SECTOR PERFORMANCE
                    </button>

                    <button className={`tablinks sub-header-font ${activeTab === "STRATEGY_INDICES" ? "active" : ""}`}
                        onClick={() => setActiveTab("STRATEGY_INDICES")} >
                        STRATEGY INDICES
                    </button>

                    <button className={`tablinks sub-header-font ${activeTab === "FO_TOP_GAINERS_LOSERS" ? "active" : ""}`}
                        onClick={() => setActiveTab("FO_TOP_GAINERS_LOSERS")} >
                        F&O TG & TL
                    </button>

                </div>
            </div>

            {/* ================= GRID ================= */}

            {/* ===== SECTORIAL INDICES ===== */}
            {activeTab === "SECTORIAL_INDICES" && (
                <div className="table-container">

                    <div className="sector-header">
                        <h2 className="sector-heading">
                            <span className="sectorial-indices-span">SECTORIAL INDICIES</span>
                        </h2>
                    </div>

                    <div className="greed-outer-container">
                        <div className="sector-grid sector-grid-sectorial">
                            {sectorData.map((item, index) => {

                                const isPositive = item.pChange >= 0;

                                return (
                                    <div key={index} className="sector-card" style={getCardStyle(item.pChange)}>

                                        <div className="sector-title">{item.index}</div>

                                        <div className="sector-values">
                                            <span className="price">
                                                {Number(item.current).toLocaleString("en-IN", {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2
                                                })}
                                            </span>

                                            <span className={`percent ${isPositive ? "pos" : "neg"}`}>
                                                {isPositive ? "+" : ""}
                                                {item.pChange}%
                                            </span>
                                        </div>

                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* ===== ALL SECTORIAL ===== */}
                    <div className="sector-header">
                        <h2 className="sector-heading">
                            <span className="sectorial-indices-span">ALL SECTORIAL INDICIES</span>
                        </h2>
                    </div>

                    <div className="sectorial-indices-grid">
                        {sectorData.map((item, index) => {

                            const isPositive = item.pChange >= 0;

                            return (
                                <div key={index} className="sectorial-indices-card">

                                    {/* HEADER */}
                                    <div
                                        className="sectorial-indices-stock-header"
                                        style={getCardStyle(item.pChange)}
                                    >
                                        <div className="sectorial-indices-stock-title">
                                            <span>{item.index}</span>

                                            <span className="price">
                                                {Number(item.current).toLocaleString("en-IN", {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2
                                                })}
                                            </span>

                                            <span className={`percent ${isPositive ? "pos" : "neg"}`}>
                                                {isPositive ? "+" : ""}
                                                {item.pChange}%
                                            </span>
                                        </div>
                                    </div>

                                    {/* BODY */}
                                    <div className="greed-outer-container">
                                        <div className="sector-grid">
                                            {(item.sectorialIndicesStockModelList || []).map((stock, index) => {

                                                const isPositive = stock.pChange >= 0;

                                                return (
                                                    <div key={index} className="sector-card" style={getCardStyle(stock.pChange)}>

                                                        <div className="sector-title">{stock.symbol}</div>

                                                        <div className="sector-values">
                                                            <span className="price">
                                                                {Number(stock.lastPrice).toLocaleString("en-IN", {
                                                                    minimumFractionDigits: 2,
                                                                    maximumFractionDigits: 2
                                                                })}
                                                            </span>

                                                            <span className={`percent ${isPositive ? "pos" : "neg"}`}>
                                                                {isPositive ? "+" : ""}
                                                                {stock.pChange}%
                                                            </span>
                                                        </div>

                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                </div>
                            );
                        })}
                    </div>

                </div>
            )}

            {/* ===== BSET SECTOR & STOCK ===== */}
            {activeTab === "BEST_STOCK" && (
                <div className="table-container">

                    <div className="sector-header">
                        <h2 className="sector-heading">
                            <span className="sectorial-indices-span">BEST SECTOR</span>
                        </h2>
                    </div>

                    <div className="greed-outer-container">
                        <div className="sector-grid sector-grid-sectorial">
                            {sectorData
                                .filter(item => item.index.toUpperCase() !== "NIFTY MEDIA")
                                .filter(item => item.pChange >= 1.0 || item.pChange <= -1.0)
                                .map((item, index) => {

                                    const isPositive = item.pChange >= 0;

                                    return (
                                        <div key={index} className="sector-card" style={getCardStyle(item.pChange)}>

                                            <div className="sector-title">{item.index}</div>

                                            <div className="sector-values">
                                                <span className="price">
                                                    {Number(item.current).toLocaleString("en-IN", {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2
                                                    })}
                                                </span>

                                                <span className={`percent ${isPositive ? "pos" : "neg"}`}>
                                                    {isPositive ? "+" : ""}
                                                    {item.pChange}%
                                                </span>
                                            </div>

                                        </div>
                                    );
                                })}
                        </div>
                    </div>

                    {/* ===== ALL SECTORIAL ===== */}
                    <div className="sector-header">
                        <h2 className="sector-heading">
                            <span className="sectorial-indices-span">BEST SECTOR STOCK</span>
                        </h2>
                    </div>

                    <div className="sectorial-indices-grid">
                        {sectorData
                            .filter(item => item.index.toUpperCase() !== "NIFTY MEDIA")
                            .filter(item => item.pChange >= 1.0 || item.pChange <= -1.0)
                            .map((item, index) => {

                                const isPositive = item.pChange >= 0;

                                return (
                                    <div key={index} className="sectorial-indices-card">

                                        {/* HEADER */}
                                        <div
                                            className="sectorial-indices-stock-header"
                                            style={getCardStyle(item.pChange)}
                                        >
                                            <div className="sectorial-indices-stock-title">
                                                <span>{item.index}</span>

                                                <span className="price">
                                                    {Number(item.current).toLocaleString("en-IN", {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2
                                                    })}
                                                </span>

                                                <span className={`percent ${isPositive ? "pos" : "neg"}`}>
                                                    {isPositive ? "+" : ""}
                                                    {item.pChange}%
                                                </span>
                                            </div>
                                        </div>

                                        {/* BODY */}
                                        <div className="greed-outer-container">
                                            <div className="sector-grid">
                                                {(item.sectorialIndicesStockModelList || [])
                                                    .filter(stock => SECTOR_SELECTED_STOCK_SET.has(stock.symbol))
                                                    // .filter(stock => stock.pChange >= 1.0 || stock.pChange <= -1.0)
                                                    .map((stock, index) => {
                                                        const isPositive = stock.pChange >= 0;

                                                        return (
                                                            <div key={index} className="sector-card" style={getCardStyle(stock.pChange)}>

                                                                <div className="sector-title">{stock.symbol}</div>

                                                                <div className="sector-values">
                                                                    <span className="price">
                                                                        {Number(stock.lastPrice).toLocaleString("en-IN", {
                                                                            minimumFractionDigits: 2,
                                                                            maximumFractionDigits: 2
                                                                        })}
                                                                    </span>

                                                                    <span className={`percent ${isPositive ? "pos" : "neg"}`}>
                                                                        {isPositive ? "+" : ""}
                                                                        {stock.pChange}%
                                                                    </span>
                                                                </div>

                                                            </div>
                                                        );
                                                    })}
                                            </div>
                                        </div>

                                    </div>
                                );
                            })}
                    </div>

                </div>
            )}

            {activeTab === "SECTOR_PERFORMANCE" && (
                <SectorPerformance />
            )}

            {/* ===== STRATEGY INDICES ===== */}
            {activeTab === "STRATEGY_INDICES" && (
                <div className="table-container">

                    {/* <div className="sector-header">
                        <h2 className="sector-heading">
                            <span className="sectorial-indices-span">STRATEGY INDICIES</span>
                        </h2>
                    </div> */}

                    {/* <div className="greed-outer-container">
                        <div className="sector-grid sector-grid-sectorial">
                            {strategyData.map((item, index) => {

                                const isPositive = item.pChange >= 0;

                                return (
                                    <div key={index} className="sector-card" style={getCardStyle(item.pChange)}>

                                        <div className="sector-title">{item.index}</div>

                                        <div className="sector-values">
                                            <span className="price">
                                                {Number(item.current).toLocaleString("en-IN", {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2
                                                })}
                                            </span>

                                            <span className={`percent ${isPositive ? "pos" : "neg"}`}>
                                                {isPositive ? "+" : ""}
                                                {item.pChange}%
                                            </span>
                                        </div>

                                    </div>
                                );
                            })}
                        </div>
                    </div> */}
                    {/* ===== ALL SECTORIAL ===== */}
                    {/* <div className="sector-header">
                        <h2 className="sector-heading">
                            <span className="sectorial-indices-span">ALL STRATEGY INDICIES</span>
                        </h2>
                    </div> */}

                    <div className="sectorial-indices-grid">
                        {strategyData.map((item, index) => {

                            const isPositive = item.pChange >= 0;

                            return (
                                <div key={index} className="sectorial-indices-card">

                                    {/* HEADER */}
                                    <div
                                        className="sectorial-indices-stock-header"
                                        style={getCardStyle(item.pChange)}
                                    >
                                        <div className="sectorial-indices-stock-title">
                                            {/* <span>{item.index}</span> */}
                                            <span className="momentum-stock">
                                                {index === 0 ? "MOMENTUM STOCK" : index === 1 ? "HIGH MOMENTUM STOCK" : `STRATEGY ${index + 1}`}
                                            </span>
                                            {/* <span className="price">
                                                {Number(item.current).toLocaleString("en-IN", {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2
                                                })}
                                            </span>

                                            <span className={`percent ${isPositive ? "pos" : "neg"}`}>
                                                {isPositive ? "+" : ""}
                                                {item.pChange}%
                                            </span> */}
                                        </div>
                                    </div>

                                    {/* BODY */}
                                    <div className="greed-outer-container">
                                        <div className="sector-grid">
                                            {(item.strategyIndicesStockModelList || []).map((stock, index) => {

                                                const isPositive = stock.pChange >= 0;

                                                return (
                                                    <div key={index} className="sector-card" style={getCardStyle(stock.pChange)}>

                                                        <div className="sector-title">{stock.symbol}</div>

                                                        <div className="sector-values">
                                                            <span className="price">
                                                                {Number(stock.lastPrice).toLocaleString("en-IN", {
                                                                    minimumFractionDigits: 2,
                                                                    maximumFractionDigits: 2
                                                                })}
                                                            </span>

                                                            <span className={`percent ${isPositive ? "pos" : "neg"}`}>
                                                                {isPositive ? "+" : ""}
                                                                {stock.pChange}%
                                                            </span>
                                                        </div>

                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {activeTab === "FO_TOP_GAINERS_LOSERS" && (
                <div className="table-container">
                    <FOTopGainerLooser />
                </div>
            )}
        </div>
    );
}
